import * as utils from "common/utils";
import * as domain from "common/domain";
import moment from "moment";

export const DEFAULT_STOCK_COUNTS = [
    { name: "July", month: 7, stockCount: 0 },
    { name: "August", month: 8, stockCount: 0 },
    { name: "September", month: 9, stockCount: 0 },
    { name: "October", month: 10, stockCount: 0 },
    { name: "November", month: 11, stockCount: 0 },
    { name: "December", month: 12, stockCount: 0 },
    { name: "January", month: 1, stockCount: 0 },
    { name: "February", month: 2, stockCount: 0 },
    { name: "March", month: 3, stockCount: 0 },
    { name: "April", month: 4, stockCount: 0 },
    { name: "May", month: 5, stockCount: 0 },
    { name: "June", month: 6, stockCount: 0 },
];

export const getEnterpriseTypeOptions = (budget, refData) => {
    const { enterprises = [] } = budget ? budget : {};
    const enterpriseTypes = refData ? refData.enterpriseTypes || [] : [];

    const enterpriseTypeOptions = enterpriseTypes.reduce((options, option) => {
        const farmHasDairy = enterprises.some((e) => e.type === "Dairy");
        const currentOptionIsDairyReplacements = option.value === "DairyReplacements";

        if (enterprises.some((e) => e.type === option.value)) {
            options.push({
                ...option,
                text: currentOptionIsDairyReplacements && farmHasDairy ? `- ${option.text}` : option.text,
                disabled: true,
                groupIndex: 1,
                groupLabel: "Already on this farm",
            });
        } else {
            options.push({
                ...option,
                text: currentOptionIsDairyReplacements && !farmHasDairy ? `- ${option.text}` : option.text,
                disabled: currentOptionIsDairyReplacements && !farmHasDairy,
                groupIndex: 0,
                groupLabel: "Not on this farm",
            });
        }
        return options;
    }, []);

    return enterpriseTypeOptions;
};

const _getMonth = (event) => {
    const date = Date.parse(`${event.month} 1, 2000`);
    return new Date(date).getMonth() + 1;
};

export const getStartWeight = (events) => {
    if (!events || events.length === 0) {
        return;
    }
    let weightEvent = events.find((e) => e.eventType === "Starting");
    weightEvent = weightEvent || events.find((e) => e.eventType === "Purchase" && e.weightKg > 0);
    return weightEvent && weightEvent.weightKg;
};

export const getStockCountsForMob = (mob) => {
    return getStockCounts(mob.events, mob.deaths);
};

export const getStockCounts = (events, deaths) => {
    if (!events || events.length === 0) {
        return DEFAULT_STOCK_COUNTS;
    }

    let runningTotal = 0;
    const stockCounts = DEFAULT_STOCK_COUNTS.map((item) => {
        const eventsByMonth = events.filter((event) => {
            if (!event.numberAnimals) return false;

            if (event.eventDate) {
                return utils.parseDate(event.eventDate, "DD MMMM").month() + 1 === item.month;
            }

            if (event.month) {
                const eventMonth = _getMonth(event);
                return eventMonth === item.month;
            }

            return false;
        });

        const monthTotal = eventsByMonth.reduce((sum, event) => {
            if (!event.numberAnimals) return 0;

            let numberAnimals = parseInt(event.numberAnimals || 0, 10);
            if (["Sale", "SaleToWorks"].includes(event.eventType)) {
                numberAnimals = -numberAnimals;
            }
            return sum + numberAnimals;
        }, 0);

        runningTotal += monthTotal - (deaths && deaths[item.name] ? deaths[item.name] : 0);

        return {
            ...item,
            stockCount: runningTotal,
        };
    });
    return stockCounts;
};

export const getLivestockDetails = (livestock, refData) => {
    switch (livestock.enterpriseType) {
        case "Sheep":
            return { class: utils.valueToText(refData.sheepStockClasses, livestock.sheepStockClass), breed: utils.valueToText(refData.sheepBreeds, livestock.sheepBreed) };
        case "DairyGrazing":
            return { class: utils.valueToText(refData.dairyGrazingStockClasses, livestock.beefStockClass), breed: utils.valueToText(refData.beefBreeds, livestock.beefBreed) };
        case "Beef":
            return { class: utils.valueToText(refData.beefStockClasses, livestock.beefStockClass), breed: utils.valueToText(refData.beefBreeds, livestock.beefBreed) };
        case "Deer":
            return { class: utils.valueToText(refData.deerStockClasses, livestock.deerStockClass), breed: utils.valueToText(refData.deerBreeds, livestock.deerBreed) };
        case "Dairy":
            return { class: utils.valueToText(refData.dairyStockClasses, livestock.dairyStockClass), breed: utils.valueToText(refData.dairyBreeds, livestock.dairyBreed) };
        case "DairyReplacements":
            return { class: utils.valueToText(refData.dairyReplacementStockClasses, livestock.dairyReplacementStockClass), breed: utils.valueToText(refData.dairyBreeds, livestock.dairyBreed) };
        case "DairyGoat":
            return { class: utils.valueToText(refData.dairyGoatStockClasses, livestock.dairyGoatStockClass), breed: utils.valueToText(refData.dairyGoatBreeds, livestock.dairyGoatBreed) };
        default:
            return { class: utils.valueToText(refData.enterpriseTypes, livestock.enterpriseType), breed: "" };
    }
};

export const showRelativeProductivity = (relProdMethod) => {
    const RELATIVE_PRODUCTIVITY_METHODS = ["Relativeyield", "Grazingdays", "Pastureassessment", "Animalassessment"];
    return RELATIVE_PRODUCTIVITY_METHODS.includes(relProdMethod);
};

export const showPercenBlockPastureProd = (blockStockRatios) => {
    return blockStockRatios === "UserDefined";
};

export const canHaveAnimals = (block) => {
    if ([domain.BlockType.ProductiveCrop, domain.BlockType.FodderCrop, domain.BlockType.ProductiveOutdoorPigs].includes(block.type)) return true;
    if (block.type === "ProductiveFruit" && block.fruit && block.fruit.swardAnimalSource === "NonFarmAnimals") return true;

    return canDistributeAnimals(block);
};

export const canDistributeAnimals = (block) => {
    if (block.type === "ProductivePasture" && block.runoffCharacteristics) return true;

    if (block.type === "ProductiveFruit" && block.fruit && block.fruit.swardAnimalSource === "EnterpriseStock") return true;

    return false;
};

export const getBlockStockRatiosOptions = (refData) => {
    const blockStockRatiosOptions =
        refData &&
        refData.blockStockRatios &&
        refData.blockStockRatios
            .map((option) => {
                switch (option.value) {
                    case "UserDefined":
                        return { value: option.value, text: "User defined for each block" };
                    case "AnimalEquivalent":
                        return { value: option.value, text: "Same as ratio of total animal intake" };
                    case "BasedOnAnimalsOnBlock":
                        return { value: option.value, text: "Based on animals present on block" };
                    default:
                        return option;
                }
            })
            .sort((a, b) => (b.value < a.value ? 1 : -1));
    return blockStockRatiosOptions;
};

export const getSaltBlockTypeOptions = (refData) => {
    const options =
        refData &&
        refData.saltBlockTypes &&
        refData.saltBlockTypes.filter((option) => {
            return option.value !== "None";
        });
    return options;
};

export const getStockClass = (livestock) => {
    switch (livestock.enterpriseType) {
        case "Sheep":
            return livestock.sheepStockClass;
        case "DairyGrazing":
        case "Beef":
            return livestock.beefStockClass;
        case "Deer":
            return livestock.deerStockClass;
        case "Dairy":
            return livestock.dairyStockClass;
        case "DairyReplacements":
            return livestock.dairyReplacementStockClass;
        case "DairyGoat":
            return livestock.dairyGoatStockClass;
        default:
            return undefined;
    }
};

export const getBreed = (livestock) => {
    switch (livestock.enterpriseType) {
        case "Sheep":
            return livestock.sheepBreed;
        case "DairyGrazing":
        case "Beef":
            return livestock.beefBreed;
        case "Deer":
            return livestock.deerBreed;
        case "Dairy":
            return livestock.dairyBreed;
        case "DairyReplacements":
            return livestock.dairyBreed;
        case "DairyGoat":
            return livestock.dairyGoatBreed;
        default:
            return undefined;
    }
};

export const isBreedingReplacementStockClass = (enterpriseType, stockClass) => {
    return (enterpriseType === "Sheep" && stockClass === "Hoggets") || (enterpriseType === "Beef" && stockClass === "HeifersBreeding");
};

export const canStockClassBeWeaned = (enterpriseType, stockClass) => {
    const dairyStockClasses = [];
    const dairyGrazingStockClasses = [];
    const dairyReplacementStockClasses = ["HeiferReplacements"];
    const sheepStockClasses = ["Lambs", "Hoggets"];
    const beefStockClasses = ["Weaners", "HeifersBreeding"];
    const deerStockClasses = ["BreedingReplacements", "Weaners"];
    const dairyGoatStockClasses = ["Replacements"];

    return (enterpriseType === "Dairy" && dairyStockClasses.includes(stockClass)) || (enterpriseType === "DairyGrazing" && dairyGrazingStockClasses.includes(stockClass)) || (enterpriseType === "DairyReplacements" && dairyReplacementStockClasses.includes(stockClass)) || (enterpriseType === "Sheep" && sheepStockClasses.includes(stockClass)) || (enterpriseType === "Beef" && beefStockClasses.includes(stockClass)) || (enterpriseType === "Deer" && deerStockClasses.includes(stockClass)) || (enterpriseType === "DairyGoat" && dairyGoatStockClasses.includes(stockClass));
};

export const sexRequired = (enterpriseType, stockClass) => {
    const dairyStockClasses = [];
    const dairyGrazingStockClasses = [];
    const dairyReplacementStockClasses = ["CalvesWeanedMixedSex"];
    const sheepStockClasses = ["Lambs"];
    const beefStockClasses = ["Weaners"];
    const deerStockClasses = ["Weaners"];
    const dairyGoatStockClasses = [];

    return (enterpriseType === "Dairy" && dairyStockClasses.includes(stockClass)) || (enterpriseType === "DairyGrazing" && dairyGrazingStockClasses.includes(stockClass)) || (enterpriseType === "DairyReplacements" && dairyReplacementStockClasses.includes(stockClass)) || (enterpriseType === "Sheep" && sheepStockClasses.includes(stockClass)) || (enterpriseType === "Beef" && beefStockClasses.includes(stockClass)) || (enterpriseType === "Deer" && deerStockClasses.includes(stockClass)) || (enterpriseType === "DairyGoat" && dairyGoatStockClasses.includes(stockClass));
};

export const ageOrWeightRequired = (enterpriseType, stockClass) => {
    const dairyStockClasses = [];
    const dairyReplacementStockClasses = ["HeiferReplacements", "CalvesWeanedMixedSex"];
    const sheepStockClasses = ["BreedingEwes", "Hoggets", "Lambs", "EwesAndHoggets", "Wethers", "Rams"];
    const beefStockClasses = ["CowsBreeding", "HeifersBreeding", "Weaners", "HeifersAndCows", "Steers", "Bulls", "DairyReplacements"];
    const dairyGrazingStockClasses = ["DairyReplacements"];
    const deerStockClasses = ["BreedingHinds", "BreedingReplacements", "Weaners", "Hinds", "Stags"];
    const dairyGoatStockClasses = ["Replacements"];

    return (enterpriseType === "Dairy" && dairyStockClasses.includes(stockClass)) || (enterpriseType === "DairyReplacements" && dairyReplacementStockClasses.includes(stockClass)) || (enterpriseType === "Sheep" && sheepStockClasses.includes(stockClass)) || (enterpriseType === "Beef" && beefStockClasses.includes(stockClass)) || (enterpriseType === "DairyGrazing" && dairyGrazingStockClasses.includes(stockClass)) || (enterpriseType === "Deer" && deerStockClasses.includes(stockClass)) || (enterpriseType === "DairyGoat" && dairyGoatStockClasses.includes(stockClass));
};

export const dairyBreeds = (breedType, livestocks, refData) => {
    return (refData[`${breedType}Breeds`] || []).map((option) => {
        if (livestocks.some((l) => l[`${breedType}Breed`] === option.value)) {
            return {
                ...option,
                groupIndex: 0,
                groupLabel: "Already on this farm",
            };
        } else {
            return {
                ...option,
                groupIndex: 2,
                groupLabel: "Not on this farm",
            };
        }
    });
};

export const monthlyTotal = (enterprise, month) => {
    let monthTotal = 0;
    (enterprise.mobs || []).forEach((mob) => {
        const events = (mob.events || []).filter((e) => e.month === month);
        monthTotal -= (mob.deaths && mob.deaths[month]) || 0;
        events.forEach((event) => {
            let numberAnimals = parseInt(event.numberAnimals, 10);
            if (["Sale", "SaleToWorks"].includes(event.eventType)) {
                numberAnimals = -numberAnimals;
            }
            monthTotal += numberAnimals;
        });
    });
    return monthTotal;
};

export const animalMonthlyCounts = (enterprise) => {
    let runningTotal = 0;
    const slots = domain.farmYear.map((month) => {
        let monthTotal = monthlyTotal(enterprise, month);
        if (enterprise.peakCowNumbers && enterprise.specificationMethod !== "MonthlyStockRec" && enterprise.type === "Dairy") {
            runningTotal = enterprise.peakCowNumbers.amount;
        } else if (enterprise.specificationMethod === "RSU" && ["Sheep", "Deer", "Beef"].includes(enterprise.type)) {
            runningTotal = enterprise.numberOfRSU;
        } else {
            runningTotal += monthTotal;
        }
        return { month, text: runningTotal === 0 ? "-" : runningTotal };
    });
    return slots;
};

export const validateLactationDates = (strMidCalvingDate, strDryDate) => {
    if (!strDryDate || !strMidCalvingDate) return [true, 0];
    const dryDate = moment(strDryDate, "DD MMMM");
    dryDate.year(2014);
    const midCalvingDate = moment(strMidCalvingDate, "DD MMMM");
    midCalvingDate.year(2014);
    midCalvingDate.year(dryDate < midCalvingDate ? 2013 : 2014);
    const days = dryDate.diff(midCalvingDate, "days");
    return [days > 99 && days < 336, days];
};

const _otherLivestockTypes = [
    {
        value: "Goats",
        text: "Goats",
        children: [
            { name: "Bucks and does to 1 yr", rsu: "0.5" },
            { name: "Angora", rsu: "1.1" },
            { name: "Feral does", rsu: "0.9" },
            { name: "Feral wethers and bucks", rsu: "0.5" },
        ],
    },
    {
        value: "Horses",
        text: "Horses",
        children: [
            { name: "Pony in light work or turned out", rsu: "6.0" },
            { name: "Pony broodmare and foal", rsu: "8.0" },
            { name: "Small hack (up to 15.2 hands) in light work", rsu: "8.0" },
            { name: "Small hack broodmare and foal", rsu: "10.0" },
            { name: "Large (500-600 kg) hack in light work", rsu: "12.0" },
            { name: "Yearling thoroughbred", rsu: "12.0" },
            { name: "Large hack broodmare and foal", rsu: "14.0" },
        ],
    },
    {
        value: "Camelids",
        text: "Alpacas/Llamas",
        children: [
            { name: "Alpacas", rsu: "0.8" },
            { name: "Llamas", rsu: "1.6" },
        ],
    },
    {
        value: "Other",
        text: "Other",
        children: [],
    },
];

export const getOtherLivestockTypeOptions = (livestocks) => {
    const livestockTypeOptions = _otherLivestockTypes.reduce((options, option) => {
        if ((livestocks || []).some((l) => l.type === option.value)) {
            options.push({
                ...option,
                groupIndex: 0,
                groupLabel: "Already on this farm",
            });
        } else {
            options.push({
                ...option,
                groupIndex: 1,
                groupLabel: "Not on this farm",
            });
        }
        return options;
    }, []);
    return livestockTypeOptions;
};

export const getOtherLivestockType = (type) => {
    return _otherLivestockTypes.find((opt) => opt.value === type);
};

export const getOtherLivestockStockClassOptions = (livestockType) => {
    if (!livestockType) return [];

    const otherLivestockType = _otherLivestockTypes.find((t) => t.value === livestockType);
    if (!otherLivestockType) return [];

    const options = otherLivestockType.children.map((stockClass) => ({
        value: stockClass.name,
        text: stockClass.name,
        groupIndex: 0,
        groupLabel: "System stock classes",
    }));

    options.push({
        value: "UserDefined",
        text: "User defined",
        groupIndex: 1,
        groupLabel: "Custom stock class",
    });

    return options;
};

export const getOtherLivestockStockClass = (name) => {
    return _otherLivestockTypes.reduce((result, option) => {
        const stockClass = option.children.find((sc) => sc.name === name);
        if (stockClass) result = stockClass;
        return result;
    }, undefined);
};

export const isCustomOtherLivestockStockClass = (livestock) => {
    if (livestock.type === "Other") return true;

    const stockClass = getOtherLivestockStockClass(livestock.stockClass);
    if (!stockClass && (livestock.stockClass || livestock.stockClass === "")) return true;

    return false;
};

export const getNonDairyGoatEnterpriesesOnPastureBlocks = (enterprises, blocks) => {
    if (!enterprises || enterprises.length === 0) return [];

    if (!blocks || blocks.length === 0) return [];

    const dairyGoat = enterprises.find((enterprise) => enterprise.type === "DairyGoat");
    if (!dairyGoat) return [];

    const isDairyGoatOnPastureBlock = blocks.filter((b) => b.type === "ProductivePasture").some((b) => b.animals.some((a) => a.enterpriseId === dairyGoat.id));
    if (!isDairyGoatOnPastureBlock) return [];

    const nonDairyGoatEnterprises = enterprises.filter((enterprise) => enterprise.type !== "DairyGoat");
    const nonDairyGoatEnterprisesOnPastureBlocks = nonDairyGoatEnterprises.reduce((results, enterprise) => {
        const enterpriseIsOnAPastureBlock = blocks.filter((b) => b.type === "ProductivePasture").some((b) => b.animals.some((a) => a.enterpriseId === enterprise.id));
        if (enterpriseIsOnAPastureBlock) results.push(enterprise);

        return results;
    }, []);
    return nonDairyGoatEnterprisesOnPastureBlocks;
};

export const getNonDairyGoatWinterpingPads = (budget) => {
    const dairyGoatEnterprise = (budget.enterprises || []).find((e) => e.type === "DairyGoat");
    if (!dairyGoatEnterprise) return [];

    return (budget.structures || []).filter((s) => ["CoveredWinteringPad", "UncoveredWinteringPad"].includes(s.type) && s.enterpriseId !== dairyGoatEnterprise.id);
};

export const hasDairyGoatWinteringPad = (budget) => {
    const dairyGoatEnterprise = (budget.enterprises || []).find((e) => e.type === "DairyGoat");
    if (!dairyGoatEnterprise) return false;

    return (budget.structures || []).some((s) => ["CoveredWinteringPad", "UncoveredWinteringPad"].includes(s.type) && s.enterpriseId === dairyGoatEnterprise.id);
};

export const hasNonDairyGoatEnterprises = (budget) => {
    const nonDairyGoatEnterprises = getNonDairyGoatEnterpriesesOnPastureBlocks(budget.enterprises, budget.blocks);
    return nonDairyGoatEnterprises.length > 0;
};

export const hasNonDairyGoatWinteringPads = (budget) => {
    const nonDairyGoatWinteringPads = getNonDairyGoatWinterpingPads(budget);
    return nonDairyGoatWinteringPads.length > 0;
};
